import type { ReactNode } from 'react'
import { BanknoteIcon, FileTextIcon } from 'lucide-react'

import { cn } from 'cn'
import { Input } from '@ems/ui/components/input'
import { Field } from '@ems/ui/patterns/field'

import { DeskSection } from '#admin/features/exhibitor/onboarding/desk-section.tsx'
import type { Settlement } from '#admin/features/exhibitor/onboarding/draft.ts'

/** What each choice says. The stand desk and the registration desk book different things. */
export type SettlementText = { description: string; paid: string; invoice: string }

const STAND_TEXT: SettlementText = {
  description: 'How the exhibitor pays for the stands.',
  paid: 'Cash or a direct bank transfer that you received. The stands are confirmed and the badges are issued now.',
  invoice: 'The exhibitor pays the invoice later. The stands stay reserved until it is paid.',
}

/**
 * How the stand is paid. One or the other: the server refuses to mark an invoiced stand paid,
 * because its invoice would then stay open with nobody to collect it. The registration desk uses
 * it too, with its own `text`, and puts its payment method in `children`.
 */
export function SettlementSection({
  settlement,
  onSettlement,
  note,
  onNote,
  locked,
  number = 5,
  text = STAND_TEXT,
  children,
}: {
  settlement: Settlement
  onSettlement: (settlement: Settlement) => void
  note: string
  onNote: (note: string) => void
  locked: boolean
  number?: number
  text?: SettlementText
  children?: ReactNode
}) {
  return (
    <DeskSection number={number} title="Settlement" description={text.description} done={locked}>
      <div role="radiogroup" aria-label="Settlement" className="grid gap-3 md:grid-cols-2">
        <Choice
          checked={settlement === 'paid'}
          disabled={locked}
          onSelect={() => onSettlement('paid')}
          icon={<BanknoteIcon />}
          title="Mark paid now"
          description={text.paid}
        />
        <Choice
          checked={settlement === 'invoice'}
          disabled={locked}
          onSelect={() => onSettlement('invoice')}
          icon={<FileTextIcon />}
          title="Issue an invoice"
          description={text.invoice}
        />
      </div>
      {settlement === 'paid' ? (
        <Field
          label="Payment reference"
          help="Optional. For example, the receipt number or the transfer reference."
        >
          {(props) => (
            <Input
              {...props}
              value={note}
              maxLength={400}
              disabled={locked}
              onChange={(event) => onNote(event.target.value)}
            />
          )}
        </Field>
      ) : null}
      {children}
    </DeskSection>
  )
}

function Choice({
  checked,
  disabled,
  onSelect,
  icon,
  title,
  description,
}: {
  checked: boolean
  disabled: boolean
  onSelect: () => void
  icon: ReactNode
  title: string
  description: string
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        'flex items-start gap-3 rounded-lg border p-4 text-left transition-colors',
        'focus-visible:ring-ring/50 outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-60',
        checked ? 'border-primary bg-primary/5 ring-primary/30 ring-1' : 'hover:bg-muted/50',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'flex size-9 shrink-0 items-center justify-center rounded-md [&_svg]:size-4.5',
          checked ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
        )}
      >
        {icon}
      </span>
      <span className="flex flex-col gap-1">
        <span className="text-sm font-semibold">{title}</span>
        <span className="text-muted-foreground text-xs">{description}</span>
      </span>
    </button>
  )
}
